import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app'
import LocationDto from '@/models/Locations/LocationDto';
import CreateContactLocationRequest from '@/models/ContactLocations/CreateContactLocationRequest';
import NoteDto from '@/models/Notes/NoteDto';
import UpdateNoteRequest from '@/models/Notes/UpdateNoteRequest';
import CreateNoteRequest from '@/models/Notes/CreateNoteRequest';
import UpdateLocationGeneralDetailsRequest from '@/models/Locations/UpdateLocationGeneralDetailsRequest';
import UpdateLocationProfileDetailsRequest from '@/models/Locations/UpdateLocationProfileDetailsRequest';
import DepartmentDto from '@/models/Departments/DepartmentDto';
import UpdateAddressDetailsRequest from '@/models/Shared/UpdateAddressDetailsRequest';
import CreateDepartmentRequest from '@/models/Departments/CreateDepartmentRequest';
import UpdateLocationHealthAndSafetyRequest from '@/models/Locations/UpdateLocationHealthAndSafetyRequest';
import RateCardDto from '@/models/RateCards/RateCardDto';
import LocationTypeDto from '@/models/LocationTypes/LocationTypeDto';
import DocumentDto from '@/models/Documents/DocumentDto';
import StorageUploadResponse from '@/models/Storage/StorageUploadResponse';
import UploadDocumentRequest from '@/models/Documents/UploadDocumentRequest';
import CreateDocumentRequest from '@/models/Documents/CreateDocumentRequest';
import UpdateDocumentRequest from '@/models/Documents/UpdateDocumentRequest';
import FileUtils from '@/lib/fileUtils';
import FileWithExpiry from '@/models/Documents/FileWithExpiry';
import JourneyTemplateDto from '@/models/JourneyTemplates/JourneyTemplateDto';
import CreateJourneyTemplateRequest from '@/models/JourneyTemplates/CreateJourneyTemplateRequest';
import UpdateJourneyTemplateRequest from '@/models/JourneyTemplates/UpdateJourneyTemplateRequest';
import ContactLocationDto from '@/models/ContactLocations/ContactLocationDto';
import ContactDto from '@/models/Contacts/ContactDto';
import DeleteContactLocationRequest from '@/models/ContactLocations/DeleteContactLocationRequest';
import ContactProfileSearchRequest from '@/models/Contacts/ContactProfileSearchRequest';
import ContactProfileDto from '@/models/Contacts/ContactProfileDto';
import ContactStatus from '@/models/Contacts/ContactStatus';
import LocationWorkerDto from '@/models/LocationWorkers/LocationWorkerDto';
import WorkerProfileDto from '@/models/Workers/WorkerProfileDto';
import WorkerProfileSearchRequest from '@/models/Workers/WorkerProfileSearchRequest';
import WorkerStatus from '@/models/Workers/WorkerStatus';
import WorkType from '@/models/Shared/WorkType';
import CreateLocationWorkerRequest from '@/models/LocationWorkers/CreateLocationWorkerRequest';
import DeleteLocationWorkerRequest from '@/models/LocationWorkers/DeleteLocationWorkerRequest';
import UpdateLocationWorkerRequest from '@/models/LocationWorkers/UpdateLocationWorkerRequest';
import NoteCategoryDto from '@/models/NoteCategories/NoteCategoryDto';
import { XeroContactSearchRequest, XeroContactSearchResponse } from '@/models/Invoicing/XeroContactSearchResult';
import UpdateLocationAccountingDetailsRequest from '@/models/Locations/UpdateLocationAccountingDetailsRequest';
import UpdateLocationPayrollSystemSettingsRequest from '@/models/Locations/UpdateLocationPayrollSystemSettingsRequest';

export interface ILocationDetailsState {
  loading: boolean; 
  currentLocation: LocationDto;
  notes: NoteDto[];
  noteCategories: NoteCategoryDto[];
  rateCards: RateCardDto[];
  locationTypes: LocationTypeDto[];
  loadingNotes: boolean;
  documents: DocumentDto[];
  storageUploadResponse: StorageUploadResponse;
  loadingDocuments: boolean;
  currentJourneyTemplate: JourneyTemplateDto;
  journeyTemplates: JourneyTemplateDto[];
  loadingJourneyTemplates: boolean;
  contactLocations: ContactLocationDto[];
  loadingContactLocations: boolean;
  companyContacts: ContactDto[];
  loadingCompanyContacts: boolean;
  locationWorkers: LocationWorkerDto[];
  loadingLocationWorkers: boolean;
  workers: WorkerProfileDto[];
  currentDepartment: DepartmentDto;
  departments: DepartmentDto[];
  loadingDepartments: boolean;
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'LocationDetails', namespaced: true })
class LocationDetails extends VuexModule implements ILocationDetailsState {
 
  public loading = true;
  public currentLocation: LocationDto = new LocationDto();
  public rateCards: RateCardDto[] = [];
  public locationTypes: LocationTypeDto[] = [];
  public notes: NoteDto[] = [];
  public noteCategories: NoteCategoryDto[] = [];
  public loadingNotes = true;
  public documents: DocumentDto[] = [];
  public storageUploadResponse: StorageUploadResponse = null;
  public loadingDocuments = true;
  public currentJourneyTemplate: JourneyTemplateDto = new JourneyTemplateDto();
  public journeyTemplates: JourneyTemplateDto[] = [];
  public loadingJourneyTemplates = true;
  public contactLocations: ContactLocationDto[] = []
  public loadingContactLocations = true;
  public companyContacts: ContactDto[] = [];
  public loadingCompanyContacts = false;
  public locationWorkers: LocationWorkerDto[] = []
  public loadingLocationWorkers = true;
  public workers: WorkerProfileDto[] = [];
  public currentDepartment: DepartmentDto = new DepartmentDto();
  public departments: DepartmentDto[] = [];
  public loadingDepartments = true;
  public loadingDialog = false;
  public loadingXeroContacts = false;
  public xeroContactSearchResults: XeroContactSearchResponse = new XeroContactSearchResponse();
  
  @Action
  public async getLocation(location: LocationDto) {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/locations/${location.id}`)
    .then(response => {
      this.context.commit("setCurrentLocation", response.data );
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async getRateCards() {
    this.setLoading(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentLocation.companyId}/location-rate-cards`)
    .then(response => {
      this.context.commit("setRateCards", response.data );
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  public async getWorkers() {
    const request = new WorkerProfileSearchRequest();
    request.statuses.push(WorkerStatus.Active);
    request.workTypes.push(WorkType.Temp);
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/workers/search`, request)
    .then(response => {
      this.context.commit("setWorkers", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  public async editLocationGeneralDetails(request: UpdateLocationGeneralDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/general-details`, request)
    .then(response => {
      this.context.commit("setCurrentLocation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editLocationProfileDetails(request: UpdateLocationProfileDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/profile-details`, request)
    .then(response => {
      this.context.commit("setCurrentLocation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

      @Action
  async editLocationAccountingDetails(request: UpdateLocationAccountingDetailsRequest) {
    this.setLoadingDialog(true);
     await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/accounting-details`, request)
    .then(response => {
      this.context.commit("setCurrentLocation", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async editLocationPayrollSystemSettings(request: UpdateLocationPayrollSystemSettingsRequest) {
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/payroll-settings`, request)
      .then(response => {
        this.context.commit("setCurrentLocation", response.data);
        this.setLoadingDialog(false);
      })
      .catch(error => {
        AppModule.handleApiError(error);
        this.setLoadingDialog(false);
      })
  }

  @Action
  public async editLocationHealthAndSafetyAssessment(request: UpdateLocationHealthAndSafetyRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/health-and-safety`, request)
    .then(response => {
      this.context.commit("setCurrentLocation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editLocationAddressDetails(request: UpdateAddressDetailsRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/address-details`, request)
    .then(response => {
      this.context.commit("setCurrentLocation", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async getDepartments() {
    this.setLoadingDepartments(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/departments`)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setDepartments", response.data);
      this.setLoadingDepartments(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDepartments(false);
    });
  }

  @Action
  public async createDepartment(request: CreateDepartmentRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/locations/${this.currentLocation.id}/departments`, request)
    .then(response => {
      this.context.commit("setDepartmentFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async updateCurrentDepartment(department: DepartmentDto){
    this.context.commit("setCurrentDepartment", department);
  }

  @Action
  async getJourneyTemplates() {
    this.setLoadingDepartments(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/journey-templates`)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setJourneyTemplates", response.data);
      this.setLoadingJourneyTemplates(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingJourneyTemplates(false);
    });
  }

  @Action
  public async createJourneyTemplate(request: CreateJourneyTemplateRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/locations/${this.currentLocation.id}/journey-templates`, request)
    .then(response => {
      this.context.commit("setJourneyTemplateFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async editJourneyTemplate(request: UpdateJourneyTemplateRequest) {
    this.setLoadingDialog(true); 
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/journey-templates/${this.currentJourneyTemplate.id}`, request)
    .then(response => {
      this.context.commit("setJourneyTemplateFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async updateCurrentJourneyTemplate(journeyTemplate: JourneyTemplateDto){
    this.context.commit("setCurrentJourneyTemplate", journeyTemplate);
  }

  @Action
  async getContactLocations() {
    this.setLoadingContactLocations(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/contacts`)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setContactLocations", response.data);
      this.setLoadingContactLocations(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingContactLocations(false);
    });
  }

  @Action
  async getCompanyContacts() {
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/companies/${this.currentLocation.companyId}/contacts?status=` + ContactStatus.Active)
    .then(response => {
      this.context.commit("setCompanyContacts", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
    //  this.setLoadingDialog(true);
    //  const request = new ContactProfileSearchRequest();
    //  request.companyId = this.currentLocation.companyId;
    // await ajax.post(`/api/v1/agency/contacts/search`, request)
    // .then(response => {
    //   if (response.data === null) {
    //     response.data = [];
    //   }
    //   this.context.commit("setCompanyContacts", response.data);
    //   this.setLoadingDialog(false);
    // })
    // .catch(error => {
    //   AppModule.handleApiError(error);
    //   this.setLoadingDialog(false);
    // });
  }


  @Action
  public async createContactLocation(request: CreateContactLocationRequest) {
    this.setLoadingDialog(true); 
    const contactId = request.contactId;
    request.contactId = undefined;
    request.locationId = this.currentLocation.id;
    await ajax.post(`/api/v1/agency/contacts/${contactId}/locations`, request)
    .then(response => {
      this.context.commit("setContactLocationFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async deleteContactLocation(request: DeleteContactLocationRequest) {
    this.setLoadingDialog(true); 
    await ajax.delete(`/api/v1/agency/contacts/${request.contactId}/locations/${request.contactLocationId}`)
    .then(response => {
      this.context.commit("setContactLocationFromDelete", request.contactLocationId);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async getLocationWorkers() {
    this.setLoadingLocationWorkers(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/workers`)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setLocationWorkers", response.data);
      this.setLoadingLocationWorkers(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingLocationWorkers(false);
    });
  }

  @Action
  public async createLocationWorker(request: CreateLocationWorkerRequest) {
    this.setLoadingDialog(true); 
    await ajax.post(`/api/v1/agency/locations/${this.currentLocation.id}/workers`, request)
    .then(response => {
      this.context.commit("setLocationWorkerFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async updateLocationWorker(request: UpdateLocationWorkerRequest){
    this.setLoadingDialog(true); 
    const locationWorkerId = request.locationWorkerId;
    request.locationWorkerId = undefined;
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/workers/${locationWorkerId}`, request)
    .then(response => {
      this.context.commit("setLocationWorkerFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  public async deleteLocationWorker(request: DeleteLocationWorkerRequest) {
    this.setLoadingDialog(true); 
    await ajax.delete(`/api/v1/agency/locations/${request.locationId}/workers/${request.locationWorkerId}`)
    .then(response => {
      this.context.commit("setLocationWorkerFromDelete", request.locationWorkerId);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    })
  }

  @Action
  async getDocuments() {
    this.setLoadingDocuments(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/documents`)
    .then(response => {
      this.context.commit("setDocuments", response.data);
      this.setLoadingDocuments(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDocuments(false);
    });
  }

  @Action
  async uploadDocument(fileWithExpiry: FileWithExpiry) {

    const fileExtension = "." + fileWithExpiry.file.name.split('.').pop();
    const contentType = FileUtils.cleanContentType(fileExtension, fileWithExpiry.file.type);
    //First get the signed URL
    const uploadRequest = new UploadDocumentRequest();
    uploadRequest.fileName = fileWithExpiry.file.name;
    uploadRequest.contentType = contentType;
    let destinationFileName = '';

    this.setLoadingDialog(true);
    //this.context.commit("setDocumentUploadUrl", null);
    await ajax.post(`/api/v1/agency/locations/${this.currentLocation.id}/documents/upload`, uploadRequest)
    .then(uploadResponse => {
      const config = {
        headers: {
          'content-type': contentType
        }
      };
      destinationFileName = uploadResponse.data.destinationFileName;
      return ajax.put(uploadResponse.data.signedUrl, fileWithExpiry.file, config)})
      .then(googleResponse => {
        const createRequest = new CreateDocumentRequest();
        createRequest.originFileName = fileWithExpiry.file.name;
        createRequest.contentType = contentType;
        createRequest.destinationFileName = destinationFileName;
        createRequest.isExpiryTracked = fileWithExpiry.isExpiryTracked;
        createRequest.expiresAt = fileWithExpiry.expiresAt;
        ajax.post(`/api/v1/agency/locations/${this.currentLocation.id}/documents`, createRequest)
        .then(response => {
          this.context.commit("setDocumentFromUpload", response.data);
          this.setLoadingDialog(false);
        })
      })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async downloadDocument(id: string) {
    this.setLoadingDialog(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/documents/${id}`)
    .then(response => {
      //download the file
      const element = document.createElement('a');
      element.setAttribute('href', response.data.signedUrl);

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);
          
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }

  @Action
  async editDocument(document: DocumentDto) {
    const request = new UpdateDocumentRequest();
    request.updatedAt = document.updatedAt;
    request.name = document.name;
    request.isExpiryTracked = document.isExpiryTracked;
    if (document.expiresAt !== null) {
      request.expiresAt = new Date(document.expiresAt).toISOString().substr(0, 10)
    } 
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/documents/${document.id}`, request)
    .then(response => {
      this.context.commit("setDocumentFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

    @Action
  async searchXeroContacts(request: XeroContactSearchRequest) {
    this.setLoadingXeroContacts(true);
    // Use the correct parameter name that matches what the backend expects
    return ajax.get(`/api/v1/agency/xero/contacts/search?search=${request.searchTerm}`)
      .then(response => {
        console.log('Raw response data:', response.data); // Add debug log
        this.context.commit("setXeroContactSearchResults", response.data);
        this.setLoadingXeroContacts(false);
        return response.data;
      })
      .catch(error => {
        console.error('API error:', error); // Add debug log
        AppModule.handleApiError(error);
        this.setLoadingXeroContacts(false);
        throw error;
      });
  }

  @Action
  async getNoteCategories() {
    //this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/note-categories?entityType=5`)
    .then(response => {
      this.context.commit("setNoteCategories", response.data);
      //this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      //this.setLoadingNotes(false);
    });
  }

  @Action
  async getNotes() {
    this.setLoadingNotes(true);
    await ajax.get(`/api/v1/agency/locations/${this.currentLocation.id}/notes`)
    .then(response => {
      this.context.commit("setNotes", response.data);
      this.setLoadingNotes(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingNotes(false);
    });
  }

  @Action
  async createNote(request: CreateNoteRequest) {
    this.setLoadingDialog(true);
    await ajax.post(`/api/v1/agency/locations/${this.currentLocation.id}/notes`, request)
    .then(response => {
      this.context.commit("setNoteFromCreate", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  async editNote(note: NoteDto) {
    const request = new UpdateNoteRequest();
    request.updatedAt = note.updatedAt
    request.description = note.description;
    this.setLoadingDialog(true);
    await ajax.put(`/api/v1/agency/locations/${this.currentLocation.id}/notes/${note.id}`, request)
    .then(response => {
      this.context.commit("setNoteFromEdit", response.data);
      this.setLoadingDialog(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoadingDialog(false);
    });
  }

  @Action
  cleanState() {
      this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.currentLocation = new LocationDto();
    this.notes = [];
    this.noteCategories = [];
    this.documents = [];
    this.departments = [];
    this.currentDepartment = new DepartmentDto();
    this.journeyTemplates = [];
    this.contactLocations = [];
    this.companyContacts = [];
    this.loading = true;
    this.loadingNotes = true;
    this.loadingDocuments = true;
    this.loadingJourneyTemplates = true;
    this.loadingContactLocations = true;
    this.loadingDepartments = true;
    this.loadingDialog = false;
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setLoadingDialog(isLoading: boolean) {
      this.loadingDialog = isLoading;
  }
  
  @Mutation
  private async setCurrentLocation(location: LocationDto) {
      this.currentLocation = location;
  }

  @Mutation
  private async setRateCards(rateCards: RateCardDto[]) {
      this.rateCards = rateCards;
  }

  @Mutation
  private async setLocationTypes(locationTypes: LocationTypeDto[]) {
      this.locationTypes = locationTypes;
  }

  @Mutation
  private async setJourneyTemplates(journeyTemplates: JourneyTemplateDto[]) {
      this.journeyTemplates = journeyTemplates;
      if (journeyTemplates.length > 0) {
        this.currentJourneyTemplate = journeyTemplates[0];
      }
  }

  @Mutation
  private async setJourneyTemplateFromCreate(journeyTemplate: JourneyTemplateDto) {
    this.journeyTemplates.push(journeyTemplate);
    this.currentJourneyTemplate = journeyTemplate;
  }

  @Mutation
  private async setJourneyTemplateFromEdit(journeyTemplate: JourneyTemplateDto) {
    this.currentJourneyTemplate = journeyTemplate;
    const index = this.journeyTemplates.findIndex((findJourneyTemplate: JourneyTemplateDto) => journeyTemplate.id === findJourneyTemplate.id);
    this.journeyTemplates.splice(index, 1, journeyTemplate);
  }

  @Mutation
  private async setCurrentJourneyTemplate(journeyTemplate: JourneyTemplateDto) {
      this.currentJourneyTemplate = journeyTemplate;
  }

  @Mutation
  private async setDepartments(departments: DepartmentDto[]) {
      this.departments = departments;
      this.departments = departments;
      if (departments.length > 0) {
        this.currentDepartment = departments[0];
      }
  }

  @Mutation
  private async setDepartmentFromCreate(department: DepartmentDto) {
    this.departments.push(department);
    this.currentDepartment = department;
  }

  @Mutation
  private async setCurrentDepartment(department: DepartmentDto) {
      this.currentDepartment = department;
  }

  @Mutation
  private async setNoteCategories(noteCategories: NoteCategoryDto[]) {
      this.noteCategories = noteCategories;
  }

  @Mutation
  private async setNotes(notes: NoteDto[]) {
      this.notes = notes;
  }

  @Mutation
  private async setNoteFromCreate(note: NoteDto) {
      this.notes.unshift(note);
  }

  @Mutation
  private async setNoteFromEdit(note: NoteDto) {
      const index = this.notes.findIndex((findNote: NoteDto) => findNote.id === note.id)
      this.notes.splice(index, 1, note);
  }

  @Mutation
  private async setLoadingNotes(isLoading: boolean) {
      this.loadingNotes = isLoading;
  }

  @Mutation
  private async setLoadingDepartments(isLoading: boolean) {
      this.loadingDepartments = isLoading;
  }

  @Mutation
  private async setLoadingJourneyTemplates(isLoading: boolean) {
      this.loadingJourneyTemplates = isLoading;
  }

  @Mutation
  private async setContactLocations(contactLocations: ContactLocationDto[]) {
      this.contactLocations = contactLocations;
  }

  @Mutation
  private async setLocationWorkers(locationWorkers: LocationWorkerDto[]) {
      this.locationWorkers = locationWorkers;
  }

  @Mutation
  private async setWorkers(workers: WorkerProfileDto[]) {
      this.workers = workers;
  }

  @Mutation
  private async setCompanyContacts(contacts: ContactDto[]) {
    this.companyContacts = [];
    const existingContactLocations = this.contactLocations.map(contactLocation => {
      return contactLocation.contactId;
    })
    for (let index = 0; index < contacts.length; index++) {
      const contact = contacts[index];
      if (existingContactLocations.indexOf(contact.id) == -1) {
        this.companyContacts.push(contact);
      }
    }
  }

  @Mutation
  private async setContactLocationFromCreate(contactLocation: ContactLocationDto) {
      this.contactLocations.unshift(contactLocation);
  }

  @Mutation
  private async setContactLocationFromDelete(contactLocationId: string) {
    this.contactLocations.forEach( (item, index) => {
      if(item.id === contactLocationId) this.contactLocations.splice(index,1);
    });
  }

  @Mutation
  private async setLocationWorkerFromCreate(locationWorker: LocationWorkerDto) {
      this.locationWorkers.unshift(locationWorker);
  }

  @Mutation
  private async setLocationWorkerFromDelete(locationWorkerId: string) {
    const index = this.locationWorkers.findIndex((findWorker: LocationWorkerDto) => locationWorkerId === findWorker.id);
    this.locationWorkers.splice(index,1);
  }

  @Mutation
  private async setLocationWorkerFromEdit(locationWorker: LocationWorkerDto) {
    const index = this.locationWorkers.findIndex((findWorker: LocationWorkerDto) => locationWorker.id === findWorker.id);
  this.locationWorkers.splice(index, 1, locationWorker);
  }

  @Mutation
  private async setLoadingContactLocations(isLoading: boolean) {
      this.loadingContactLocations = isLoading;
  }

  @Mutation
  private async setLoadingLocationWorkers(isLoading: boolean) {
      this.loadingLocationWorkers = isLoading;
  }

  @Mutation
  private async setDocuments(documents: DocumentDto[]) {
      this.documents = documents;
  }

  @Mutation
  private async setDocumentFromUpload(document: DocumentDto) {
      this.documents.push(document);
  }

  @Mutation
  private async setDocumentFromEdit(document: DocumentDto) {
      const index = this.documents.findIndex((findDocument: DocumentDto) => findDocument.id === document.id)
      this.documents.splice(index, 1, document);
  }

    @Mutation
    private async setLoadingXeroContacts(isLoading: boolean) {
        this.loadingXeroContacts = isLoading;
    }

    @Mutation
    private async setXeroContactSearchResults(results: XeroContactSearchResponse) {
        this.xeroContactSearchResults = results;
    }

  @Mutation
  private async setLoadingDocuments(isLoadingDocuments: boolean) {
      this.loadingDocuments = isLoadingDocuments;
  }
}

export const LocationDetailsModule = getModule(LocationDetails)
